// src/hooks/useTwilioVoiceDevice.ts
//
// Phase 2/3 — Twilio Browser Voice device lifecycle + outbound call hook.
//
// This is the foundation for in-app calling. The hook owns:
//
//   1. Mic permission detection + request flow
//   2. Voice token fetch (via the Phase 1 endpoint)
//   3. Twilio Device construction + register/destroy lifecycle
//   4. Token refresh on `tokenWillExpire`
//   5. Cleanup on unmount / workspace switch / capability loss
//   6. A typed readiness model the UI can render directly
//
// Phase 3 additions (real, wired controls only):
//   - connectToNumber(phone, ctx) → places an outbound call via
//     Device.connect({ params: { PhoneNumber } }). The PhoneNumber custom
//     param is read by the server-side TwiML controller (handleVoiceTwiml)
//     to build the <Dial> response. We deliberately use `PhoneNumber`
//     (not `To`) so we don't collide with Twilio's own `To` param.
//   - activeCall state machine with `connecting` → `in_call` → `ended`/`failed`
//   - mute()/unmute() wired to Call.mute()
//   - endCall() wired to Call.disconnect()
//   - elapsed-duration timer driven by Date.now() interval
//
// Out of scope for Phase 3 (would be fake controls if added now):
//   - Hold (Twilio Voice SDK has no first-class hold; would require
//     re-routing through a TwiML conference)
//   - DTMF keypad (no consumers asked for it; keep CallShell minimal)
//   - Speaker selector (out of scope per spec)
//   - Incoming call ringing UI
//   - Multiple simultaneous calls
//
// HOW IT COMPOSES
//
//   const {
//     status,           // discriminated union — drives the readiness card
//     isReady,          // convenience boolean: status.kind === 'ready'
//     refresh,          // re-init: re-fetch token + re-register
//     requestMicAccess, // opens the browser mic prompt
//   } = useTwilioVoiceDevice();
//
// LIFECYCLE STATE MACHINE
//
//   idle (initial)
//     └─▶ checking_mic
//           ├─▶ mic_unsupported (terminal-ish; user can't talk in browser)
//           ├─▶ mic_denied      (terminal until user fixes browser settings)
//           ├─▶ mic_prompt      (button: "Grant microphone access")
//           └─▶ fetching_token  (mic granted)
//                 ├─▶ token_unauthorized   (401 — session dead)
//                 ├─▶ capability_denied    (403 — phone.call not granted)
//                 ├─▶ twilio_not_configured       (503 — integration missing)
//                 ├─▶ twilio_voip_not_configured  (503 — VoIP fields missing)
//                 ├─▶ token_error          (network/timeout/other)
//                 └─▶ registering
//                       ├─▶ ready          (Device.register() succeeded)
//                       └─▶ device_error   (registration failed)
//
//   At any point, `refresh()` resets to `checking_mic` and walks the
//   chain again. Token expiry triggers a silent re-fetch via
//   Device.updateToken — no UI flicker.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useFetchVoiceTokenMutation } from '../api/telephonyApi';
import { useTelephonyAvailability } from './useTelephonyAvailability';
import {
  getMicrophonePermissionState,
  requestMicrophonePermission,
  subscribeToMicrophonePermission,
  type MicPermissionState,
} from '../utils/microphonePermission';
// Inbound calling (ADDITIVE). Context-only module — no cycle, no components.
import { useSharedTelephony } from '../telephony/telephonyContext';

// ============================================================
// Twilio Voice SDK shape (subset we use)
// ============================================================
//
// We avoid importing types directly from `@twilio/voice-sdk` so the SDK
// doesn't get pulled into bundles that don't use this hook. Defined as
// a structural interface here.
export interface TwilioCall {
  on: (event: string, handler: (...args: unknown[]) => void) => void;
  removeAllListeners: () => void;
  disconnect: () => void;
  mute: (shouldMute: boolean) => void;
  isMuted?: () => boolean;
  status?: () => string;
  /** Phase-2B DTMF — sends touch-tones over the active call (Twilio SDK). */
  sendDigits?: (digits: string) => void;

  // ── Inbound calling (ADDITIVE, Phase IV) ──────────────────────────────────
  // Present on an INCOMING Call in Voice SDK 2.x. Verified against the
  // installed @twilio/voice-sdk 2.18.1: Call#accept, Call#reject, and the
  // `parameters` / `customParameters` bags are all part of the public API.
  // Optional here because an OUTGOING Call never has them called on it.
  /** Answer the ringing call. */
  accept?: () => void;
  /** Decline the ringing call — the server's <Dial action> then sees no-answer. */
  reject?: () => void;
  /** Twilio-populated call params (CallSid, From, To …). */
  parameters?: Record<string, string>;
  /** Custom params our own inbound TwiML attached to the <Client> noun. */
  customParameters?: Map<string, string> | Record<string, string>;
}

// AudioHelper subset (Twilio Voice SDK 2.x). We interact structurally so the
// SDK types don't leak into the hook signature. Present on `Device.audio`.
interface TwilioAudioHelper {
  availableInputDevices: Map<string, MediaDeviceInfo>;
  availableOutputDevices: Map<string, MediaDeviceInfo>;
  isOutputSelectionSupported: boolean;
  /** The active input MediaDeviceInfo, when known. */
  inputDevice?: MediaDeviceInfo | null;
  setInputDevice: (deviceId: string) => Promise<void>;
  speakerDevices: {
    set: (deviceIds: string | string[]) => Promise<void> | void;
    get: () => Set<MediaDeviceInfo>;
  };
  on: (event: string, handler: (...args: unknown[]) => void) => void;
  removeListener?: (event: string, handler: (...args: unknown[]) => void) => void;
}

export interface TwilioDeviceInstance {
  register: () => Promise<void>;
  destroy: () => void;
  updateToken: (token: string) => void;
  on: (event: string, handler: (...args: unknown[]) => void) => void;
  removeAllListeners: () => void;
  connect: (options: { params: Record<string, string> }) => Promise<TwilioCall>;
  /** Present on Voice SDK 2.x — mic/speaker enumeration + selection. */
  audio?: TwilioAudioHelper;
  /**
   * ADDITIVE (Phase IV). Device extends EventEmitter in Voice SDK 2.x, so this
   * exists. The TelephonyProvider uses it to detach EXACTLY its own 'incoming'
   * handler — calling removeAllListeners() there would also rip out this hook's
   * registered / error / tokenWillExpire handlers.
   */
  removeListener?: (event: string, handler: (...args: unknown[]) => void) => void;
}

// ============================================================
// Types
// ============================================================

/**
 * Discriminated union of every possible state the Voice device can be in.
 * UI components should switch on `kind` and render accordingly.
 */
export type VoiceDeviceStatus =
  | { kind: 'idle' }
  | { kind: 'checking_mic' }
  | { kind: 'mic_unsupported' }
  | { kind: 'mic_denied' }
  | { kind: 'mic_prompt' }
  | { kind: 'fetching_token' }
  | { kind: 'token_unauthorized'; message: string }
  | { kind: 'capability_denied'; message: string }
  | { kind: 'twilio_not_configured'; message: string }
  /**
   * The workspace's Twilio Voice provider is a Provider-Sync mirror
   * from Outbound. The row exists and reads healthy in the Providers
   * page — but CRM never received credentials (by design). Calling
   * from CRM is not enabled today; the readiness card surfaces this
   * honestly instead of pretending Twilio is missing.
   */
  | { kind: 'twilio_managed_by_outbound'; message: string }
  /**
   * Option B — the workspace's Twilio row is Outbound-managed, and the
   * CRM voice-token controller tried to proxy to Outbound but the
   * proxy call failed (Outbound down, service token missing, TwiML
   * App SID missing on Outbound, etc.). Distinct from
   * `twilio_managed_by_outbound` (Option A "cannot proceed" default)
   * so the readiness card can render the specific reason.
   */
  | { kind: 'twilio_managed_by_outbound_unavailable'; message: string; reason?: string }
  | { kind: 'twilio_voip_not_configured'; message: string; missingFields: string[] }
  | { kind: 'token_error'; message: string }
  | { kind: 'registering' }
  | {
      kind: 'ready';
      identity: string;
      twimlAppSid: string;
      tokenExpiresAt: string;
      /** Workspace voice recording mode — drives the "Recording" badge. */
      recordingMode: 'off' | 'always';
      /**
       * Phase 3.5 — workspace recording disclosure flag. True if the
       * server will play "This call may be recorded." before the dial.
       * Surfaced so the readiness card can show admins exactly what
       * will happen on the next call.
       */
      playRecordingDisclosure: boolean;
      /**
       * Inbound calling (ADDITIVE, Phase IV). True when the server put
       * `incomingAllow` in this token's VoiceGrant. Optional: absent === false,
       * so every existing `ready` fixture and every older server is read as
       * outgoing-only.
       */
      incomingAllow?: boolean;
      /**
       * Option B — 'outbound' when the token was minted via the CRM →
       * Outbound proxy for an Outbound-managed provider. 'crm' (or
       * absent) for native CRM Twilio. Drives the "Managed by Outbound"
       * caption on the ready state so operators know where the
       * credentials live.
       */
      managedBy?: 'crm' | 'outbound';
    }
  | { kind: 'device_error'; message: string };

/**
 * Discriminated union for the per-call state machine. Independent from
 * `VoiceDeviceStatus` because device readiness and call lifecycle are
 * orthogonal — the device can be `ready` while a call is `in_call`, and
 * a failed call should not invalidate the device.
 */
/**
 * Which way the call is flowing.
 *
 * ADDITIVE (inbound calling, Phase IV). Optional on every variant, and every
 * pre-existing construction site sets it to 'outbound', so existing consumers,
 * fixtures and exhaustive switches are unaffected. Absent === 'outbound'.
 */
export type CallDirection = 'inbound' | 'outbound';

export type ActiveCallState =
  | { kind: 'idle' }
  | {
      kind: 'connecting';
      toNumber: string;
      contactId?: string;
      contactName?: string;
      startedAt: number;
      direction?: CallDirection;
      /** See in_call.outboundCallId — populated on the outbound-managed branch. */
      outboundCallId?: string;
    }
  | {
      kind: 'in_call';
      toNumber: string;
      contactId?: string;
      contactName?: string;
      startedAt: number;
      answeredAt: number;
      muted: boolean;
      durationSeconds: number;
      direction?: CallDirection;
      /**
       * ADDITIVE (Phase 2 — mid-call recording). The Twilio Call SID as
       * exposed by the SDK on the active call's parameters. Present once
       * Twilio has assigned it (post-answer); absent otherwise. The
       * mid-call recording start endpoint uses this to identify the
       * call for the Twilio recordings.create() request.
       */
      twilioCallSid?: string;
      /**
       * Phase 1 linking — the CRM's own outboundCallId (from prepare).
       * Populated on the Outbound-managed branch by
       * `connectToOutboundCall` so downstream recording UI can pass it
       * to `/api/telephony/calls/:callSid/recording/start` and let the
       * backend link the new CallRecording to the placeholder
       * CallSession (which has `twilioCallSid: null` until the
       * completion callback lands). Absent for CRM-native calls.
       */
      outboundCallId?: string;
      /**
       * ADDITIVE (Phase IV). Twilio is re-establishing the media connection.
       * Sourced from the SDK's `reconnecting` / `reconnected` Call events
       * (both verified present in @twilio/voice-sdk 2.18.1).
       *
       * Wired for INBOUND calls only. Outgoing calls never set it, so it stays
       * `undefined` there and every existing outgoing surface renders exactly
       * as before — this does not retrofit new behaviour onto outgoing calls.
       */
      reconnecting?: boolean;
    }
  | {
      kind: 'ended';
      toNumber: string;
      contactId?: string;
      contactName?: string;
      durationSeconds: number;
      direction?: CallDirection;
    }
  | {
      kind: 'failed';
      toNumber: string;
      contactId?: string;
      contactName?: string;
      message: string;
      direction?: CallDirection;
    };

export interface ConnectCallContext {
  contactId?: string;
  contactName?: string;
  /**
   * ADDITIVE (per-call outgoing recording). Optional per-call recording
   * preference for THIS outgoing call only. Sent to the TwiML controller
   * via Device.connect's custom params.
   *
   *   undefined → workspace default (existing behaviour, byte-identical).
   *   true      → user opted IN for this call. Honoured only when the
   *               workspace mode is 'off' (see server compliance lock).
   *   false     → user opted OUT for this call. Ignored by the server
   *               when the workspace mode is 'always' — compliance
   *               settings cannot be silently overridden.
   */
  record?: boolean;
}

/** A selectable audio device (mic or speaker), projected from the SDK. */
export interface AudioDeviceOption {
  deviceId: string;
  label: string;
}

/**
 * Phase-3 audio-device state, projected from the shared Device's AudioHelper.
 * Never exposes the AudioHelper itself. `outputSelectionSupported` is false on
 * browsers where output selection is unavailable (Safari / Firefox) — the UI
 * hides the speaker selector honestly in that case.
 */
export interface AudioDevicesState {
  inputs: AudioDeviceOption[];
  outputs: AudioDeviceOption[];
  selectedInputId: string | null;
  selectedOutputId: string | null;
  outputSelectionSupported: boolean;
  loading: boolean;
  error: string | null;
}

const EMPTY_AUDIO_DEVICES: AudioDevicesState = {
  inputs: [],
  outputs: [],
  selectedInputId: null,
  selectedOutputId: null,
  outputSelectionSupported: false,
  loading: false,
  error: null,
};

/**
 * Metadata for an accepted INBOUND call. (ADDITIVE — inbound calling, Phase IV.)
 * Everything here is derived server-side or from the Twilio Call's own
 * parameters — never from arbitrary browser state.
 */
export interface IncomingCallMeta {
  /** The PSTN caller, E.164. Becomes `toNumber` in the shared call state. */
  fromPhone: string;
  contactId?: string;
  contactName?: string;
}

/**
 * Engine options. (ADDITIVE.) Both default off, so calling the engine with no
 * options behaves exactly as the hook did before inbound calling existed.
 */
export interface UseTwilioVoiceDeviceEngineOptions {
  /**
   * When true the engine does NOT construct, register, or own a Device — it
   * stays idle. Used by the context wrapper so that when a global
   * TelephonyProvider owns the one Device, the per-component hook calls
   * collapse into no-ops instead of each spawning a rival Device.
   */
  suspended?: boolean;

  /**
   * Called with the live Device immediately after construction (before
   * `register()`, so no incoming event can be missed) and with `null` when the
   * Device is torn down. ONLY the TelephonyProvider passes this — it is how the
   * provider attaches `incoming` handling without the Device ever appearing on
   * the public context.
   */
  onDeviceChange?: (device: TwilioDeviceInstance | null) => void;
}

/**
 * Provider-only surface. Deliberately NOT part of `UseTwilioVoiceDeviceResult`,
 * so a raw Twilio Call object can never be reached through the public context.
 */
export interface TwilioVoiceEngineInternals {
  /**
   * Adopt an accepted inbound Call into the SAME call state machine used by
   * outgoing calls, so mute / unmute / hang-up / DTMF / timer / audio-device
   * selection all work through the existing controls with no second
   * implementation.
   */
  attachIncomingCall: (call: TwilioCall, meta: IncomingCallMeta) => void;
}

// ============================================================
// switchProvider result contract
// ============================================================
//
// A typed discriminated union so ContactWorkspaceCallColumn can render
// the exact reason without having to string-match error messages, and
// so tests can assert on stable codes.

export type SwitchProviderErrorCode =
  | 'OUTBOUND_PROVIDER_ID_MISSING'
  | 'TOKEN_PROVIDER_MISMATCH'
  | 'VOICE_PROVIDER_NOT_FOUND'
  | 'VOICE_PROVIDER_SUSPENDED'
  | 'VOICE_PROVIDER_WORKSPACE_MISMATCH'
  | 'PROVIDER_NOT_VOICE'
  | 'PROVIDER_VOIP_CONFIG_INCOMPLETE'
  | 'TWILIO_MANAGED_BY_OUTBOUND_UNAVAILABLE'
  | 'DEVICE_REBUILD_FAILED'
  | 'TOKEN_ERROR';

export type SwitchProviderResult =
  | {
      ok: true;
      /** Echo of the exact provider the freshly-minted token is bound to. */
      outboundProviderId: string;
      /** TwiML App SID the token routes Device.connect through. */
      twimlAppSid: string;
    }
  | {
      ok: false;
      code: SwitchProviderErrorCode;
      /** Server-provided message, safe to surface to the operator. */
      message: string;
      /**
       * When the caller can prove the specific provider they asked for
       * (they always can), this is set to that id for logging.
       */
      requestedProviderId?: string;
      /** When Outbound echoed a DIFFERENT id, this carries it. */
      echoedProviderId?: string;
    };

export interface UseTwilioVoiceDeviceResult {
  /** The current device readiness state (discriminated union — switch on `kind`). */
  status: VoiceDeviceStatus;
  /** Convenience: true iff status.kind === 'ready'. */
  isReady: boolean;
  /** The current call state — independent of device readiness. */
  activeCall: ActiveCallState;
  /**
   * Re-init the device. Resets to `checking_mic` and walks the entire
   * lifecycle again. Used by the readiness card's "Retry" button.
   */
  refresh: () => Promise<void>;
  /**
   * Open the browser microphone permission prompt. Only meaningful when
   * status.kind === 'mic_prompt'. After the user responds we refresh.
   */
  requestMicAccess: () => Promise<void>;
  /**
   * Place an outbound call via the registered Twilio Device. Throws if
   * the device is not ready or another call is already in progress.
   * Wires Call event listeners that drive the activeCall state machine.
   */
  connectToNumber: (toNumber: string, ctx?: ConnectCallContext) => Promise<void>;
  /**
   * Outbound-managed browser call. Places the call with `Device.connect`
   * using the exact custom params (`To`, `CallId`) that Outbound's
   * TwiML handler resolves via `OutboundMessage.findByPk(CallId)`.
   * All lifecycle state (connecting → in_call → ended/failed) reuses
   * the SAME machinery as `connectToNumber`.
   */
  connectToOutboundCall: (
    args: { callId: string; toNumber: string },
    ctx?: ConnectCallContext,
  ) => Promise<void>;
  /**
   * Per-provider browser calling — re-mint the Voice access token against
   * the picked WorkspaceProvider and rebuild the Device so subsequent
   * `Device.connect()` calls route through THAT provider's Twilio account
   * + TwiML App SID. The complete sequence a caller must run before
   * placing a per-provider call is:
   *
   *     prepare()  → verify prepared.workspaceProviderId
   *     switchProvider(id)  → verify result.outboundProviderId
   *     connectToOutboundCall()
   *
   * Fails closed with a typed result when:
   *   - the token mint returned a mismatched echo
   *     (`TOKEN_PROVIDER_MISMATCH`);
   *   - the token mint failed for a provider-specific reason
   *     (`VOICE_PROVIDER_NOT_FOUND` / `VOICE_PROVIDER_SUSPENDED` /
   *     `PROVIDER_VOIP_CONFIG_INCOMPLETE` / …); or
   *   - the SDK refused the new token / rebuild
   *     (`DEVICE_REBUILD_FAILED`).
   *
   * Never silently falls back to the previous or default token.
   */
  switchProvider: (
    outboundProviderId: string,
  ) => Promise<SwitchProviderResult>;
  /** Mute the local microphone on the active call. No-op if no active call. */
  mute: () => void;
  /** Unmute the local microphone on the active call. */
  unmute: () => void;
  /** Disconnect the active call. */
  endCall: () => void;
  /** Reset activeCall to idle (e.g. after the user dismisses the ended/failed shell). */
  clearActiveCall: () => void;
  /**
   * Phase-2B DTMF — send touch-tones on the active call. Accepts only the
   * characters 0-9, * and #. Returns true if the digits were sent, false when
   * there is no active call or the input is invalid. Never throws.
   */
  sendDigits: (digits: string) => boolean;
  /** Phase-3 — mic/speaker devices projected from the shared Device. */
  audioDevices: AudioDevicesState;
  /** Select the active microphone. Returns false when unsupported / no device. */
  setInputDevice: (deviceId: string) => Promise<boolean>;
  /** Select the active speaker. Returns false when output selection is unsupported. */
  setOutputDevice: (deviceId: string) => Promise<boolean>;
  /** Re-enumerate audio devices from the SDK (also runs on `deviceChange`). */
  refreshAudioDevices: () => Promise<void>;
}

// ============================================================
// Hook
// ============================================================

/**
 * Twilio browser Voice device lifecycle hook.
 *
 * Composes with `useTelephonyAvailability` so we don't duplicate the
 * "is Twilio connected at all" check that powers the SMS gating —
 * if telephony is wholly unavailable for the workspace, this hook
 * also reports the appropriate not-configured state without ever
 * touching the network.
 *
 * Cleanup contract:
 *   - The created `Device` instance is destroyed on unmount, on
 *     workspace switch, and on capability loss. The same instance
 *     is reused across token refreshes.
 *   - We deliberately ref-track the device so React strict-mode
 *     double-renders don't leak duplicate registrations.
 */
export function useTwilioVoiceDeviceEngine(
  options: UseTwilioVoiceDeviceEngineOptions = {}
): UseTwilioVoiceDeviceResult & TwilioVoiceEngineInternals {
  const suspended = options.suspended === true;

  // Keep the latest callback in a ref so it never re-triggers `initialize`
  // (which would tear down and rebuild the Device on every provider render).
  const onDeviceChangeRef = useRef(options.onDeviceChange);
  onDeviceChangeRef.current = options.onDeviceChange;

  const [status, rawSetStatus] = useState<VoiceDeviceStatus>({ kind: 'idle' });
  // Mirror of `status` for reads that occur AFTER an awaited state
  // transition (e.g., `switchProvider` reading readiness after
  // `initialize()` resolves). React batches setState updates so a
  // stale-closure `status` would still show the pre-transition value —
  // the ref is the only reliable synchronous readout. Kept in sync by
  // `setStatus` (below) which updates the ref BEFORE queueing the React
  // render, so an awaited step that immediately reads the ref sees the
  // just-written value without waiting for a re-render.
  const statusRef = useRef<VoiceDeviceStatus>(status);
  const setStatus = useCallback(
    (
      next:
        | VoiceDeviceStatus
        | ((prev: VoiceDeviceStatus) => VoiceDeviceStatus),
    ) => {
      if (typeof next === 'function') {
        rawSetStatus((prev) => {
          const value = next(prev);
          statusRef.current = value;
          return value;
        });
      } else {
        statusRef.current = next;
        rawSetStatus(next);
      }
    },
    [],
  );
  const [fetchVoiceToken] = useFetchVoiceTokenMutation();

  // Compose with the existing telephony availability hook so we get
  // workspaceId, capability flags, and the early-bail "no Twilio at
  // all" signal for free.
  const {
    canMakeCalls,
    canViewCalls,
    workspaceId,
    isLoading: telephonyLoading,
    reason: telephonyReason,
  } = useTelephonyAvailability();

  // Refs for the long-lived Device instance + the latest workspaceId
  // we registered for. We use refs (not state) because re-renders
  // should never race the device construction.
  //
  // The `unknown` typing on the ref avoids importing the @twilio/voice-sdk
  // types directly into the hook signature — the type-only import lives
  // inside the dynamic import below so the SDK doesn't get bundled into
  // any consumer that doesn't actually use this hook.
  const deviceRef = useRef<TwilioDeviceInstance | null>(null);

  // The currently in-flight Call object returned by Device.connect(). We
  // hold a ref to it (not state) so the React identity of the hook result
  // doesn't change every time we attach a listener — only the activeCall
  // discriminated union changes.
  const callRef = useRef<TwilioCall | null>(null);

  // Per-call state machine. Independent of device readiness — see
  // `ActiveCallState` for the rationale.
  const [activeCall, setActiveCall] = useState<ActiveCallState>({ kind: 'idle' });

  // Tick interval ref for the elapsed-duration timer. We start it when
  // the call is answered and clear it on disconnect/cleanup.
  const tickIntervalRef = useRef<number | null>(null);
  const registeredWorkspaceIdRef = useRef<string | null>(null);
  const isMountedRef = useRef(true);

  // Per-provider browser calling. `initialize()` and the `tokenWillExpire`
  // refresh path both consult this ref when they mint a token so the JWT is
  // bound to the CRM operator's picked provider (their WorkspaceProvider.id),
  // not the workspace default. `null` = no explicit selection → Outbound
  // resolves the workspace default (byte-identical to the pre-selection
  // browser path). `switchProvider(id)` updates this ref then walks the
  // full initialize() lifecycle (destroy → mint → new Device → register)
  // so a mid-session provider change lands in the Twilio SDK cleanly.
  const activeProviderIdRef = useRef<string | null>(null);
  // Live echo of the provider the CURRENT registered device's token was
  // minted against. Read by `switchProvider` so a caller can quickly
  // detect a no-op (same provider) without paying for a full destroy +
  // remint + register. Set by `initialize()` once the token is loaded.
  const currentProviderIdRef = useRef<string | null>(null);

  // Mic permission state — separate from `status` so we can subscribe
  // to changes (user toggles in browser settings) without re-walking
  // the whole lifecycle.
  const [micState, setMicState] = useState<MicPermissionState>('unknown');

  // Phase-3 audio devices — projected from the shared Device's AudioHelper.
  const [audioDevices, setAudioDevices] = useState<AudioDevicesState>(EMPTY_AUDIO_DEVICES);
  // Ref to the deviceChange handler so we remove EXACTLY the listener we
  // registered — no duplicates, clean teardown on device replacement.
  const audioChangeHandlerRef = useRef<((...args: unknown[]) => void) | null>(null);

  // ----------------------------------------------------------------
  // Internal: walk the lifecycle
  // ----------------------------------------------------------------
  const initialize = useCallback(async () => {
    if (!isMountedRef.current) return;

    // 0. Suspended: a global TelephonyProvider already owns the one Device for
    //    this user/workspace/tab. Do nothing — no token fetch, no mic prompt,
    //    no Device. The wrapper below returns the provider's shared value
    //    instead of this engine's, so callers see a live device either way.
    //    This is what collapses the five historical call sites into one Device.
    if (suspended) {
      setStatus({ kind: 'idle' });
      return;
    }

    // 1. Central-capability short-circuits removed. The sidebar tab and the
    //    PhonePage no_permission gate were relaxed earlier so that CRM users
    //    with a workspace-configured Twilio provider can access telephony
    //    even when Central hasn't granted phone.read / phone.call. The
    //    server-side /api/telephony/voice/token endpoint still enforces
    //    those capabilities, so unauthorized users still get a hard 403
    //    from the mint — the browser just no longer refuses to try.

    // 2. Bail early on workspace-level Twilio gaps. We rely on
    //    useTelephonyAvailability to tell us whether the workspace
    //    has a connected Twilio integration at all — no point asking
    //    for the mic if there's no point fetching a token.
    if (!workspaceId) {
      setStatus({ kind: 'idle' });
      return;
    }
    if (telephonyLoading) {
      // Wait for the upstream availability hook to settle.
      return;
    }
    if (telephonyReason === 'no_integration' || telephonyReason === 'integration_error') {
      setStatus({
        kind: 'twilio_not_configured',
        message:
          'Twilio is not configured for this workspace. Connect Twilio in Settings → Integrations.',
      });
      return;
    }

    // 3. Mic permission check.
    setStatus({ kind: 'checking_mic' });
    const mic = await getMicrophonePermissionState();
    setMicState(mic);

    if (mic === 'unsupported') {
      setStatus({ kind: 'mic_unsupported' });
      return;
    }
    if (mic === 'denied') {
      setStatus({ kind: 'mic_denied' });
      return;
    }
    if (mic === 'prompt') {
      setStatus({ kind: 'mic_prompt' });
      return;
    }
    // mic === 'granted' or 'unknown' (older Safari) — proceed.

    // 4. Fetch voice token from Phase 1 endpoint.
    //
    // Per-provider browser calling: when the operator has explicitly
    // picked a provider (via `switchProvider(id)` — see below), forward
    // that WorkspaceProvider.id so the mint is bound to that Twilio
    // account. No selection → Outbound falls back to the workspace
    // default, byte-identical to the pre-selector browser path.
    const providerHint = activeProviderIdRef.current;
    setStatus({ kind: 'fetching_token' });
    let tokenResult;
    try {
      tokenResult = await fetchVoiceToken(
        providerHint ? { outboundProviderId: providerHint } : undefined,
      ).unwrap();
    } catch (err) {
      if (!isMountedRef.current) return;
      setStatus(mapTokenErrorToStatus(err));
      return;
    }

    // Snapshot the echoed provider id so `switchProvider` can short-circuit
    // on identical selections and `connectToOutboundCall` sites can assert
    // the current device matches the pick they're about to place a call with.
    currentProviderIdRef.current =
      typeof (tokenResult as { outboundProviderId?: string }).outboundProviderId ===
        'string' &&
      (tokenResult as { outboundProviderId?: string }).outboundProviderId!.length > 0
        ? ((tokenResult as { outboundProviderId?: string })
            .outboundProviderId as string)
        : null;

    if (!isMountedRef.current) return;

    // 5. Construct the Twilio Device. Dynamic import so the SDK isn't
    //    bundled until the user actually visits a page that uses this
    //    hook (currently only /phone).
    setStatus({ kind: 'registering' });
    let DeviceCtor: new (
      token: string,
      options?: Record<string, unknown>
    ) => TwilioDeviceInstance;
    try {
      const sdkModule = await import('@twilio/voice-sdk');
      DeviceCtor = (sdkModule as { Device: typeof DeviceCtor }).Device;
    } catch (err) {
      if (!isMountedRef.current) return;
      setStatus({
        kind: 'device_error',
        message:
          err instanceof Error
            ? `Failed to load Twilio Voice SDK: ${err.message}`
            : 'Failed to load Twilio Voice SDK.',
      });
      return;
    }

    // Destroy any prior device first (workspace switch, refresh).
    if (deviceRef.current) {
      try {
        deviceRef.current.removeAllListeners();
        deviceRef.current.destroy();
      } catch {
        // ignore — destroying an already-destroyed device throws
      }
      deviceRef.current = null;
      // Tell the provider the old Device is gone so it drops its incoming
      // listeners with it — otherwise a workspace switch would leave a handler
      // bound to a destroyed Device.
      try {
        onDeviceChangeRef.current?.(null);
      } catch {
        // ignore
      }
    }

    let device;
    try {
      device = new DeviceCtor(tokenResult.token, {
        // Log level. Kept at 'warn' for production: 'debug' makes the Twilio
        // Voice SDK print raw signaling frames to the browser console, and the
        // registration/listen frames carry the access-token JWT — so 'debug'
        // can surface the token in any user's console. 'warn' still bubbles
        // 'Unknown signaling error: { code, message }' at error level, which is
        // enough to diagnose registration failures without leaking the token.
        logLevel: 'warn',
        // Use the finer-grained signaling error mapping. Without this,
        // any Twilio server error that doesn't already map to a known
        // SDK error class becomes a generic ConnectionError(53000),
        // which throws away the real reason. With this on, the SDK
        // walks the full error code → constructor map, so we get the
        // specific error class (e.g. AccessTokenInvalid, RegistrationError)
        // bubbled up to the 'error' event handler. Cheap and strictly
        // more informative.
        // https://www.twilio.com/docs/voice/sdks/javascript/v2/migrating-from-twilio-client#use-improved-signaling-error-codes
        enableImprovedSignalingErrorPrecision: true,
      });
    } catch (err) {
      if (!isMountedRef.current) return;
      setStatus({
        kind: 'device_error',
        message:
          err instanceof Error
            ? `Failed to construct device: ${err.message}`
            : 'Failed to construct device.',
      });
      return;
    }

    // Wire lifecycle events. We use string-keyed `on` so the hook
    // doesn't depend on the SDK's named exports for event types.
    device.on('registered', () => {
      if (!isMountedRef.current) return;
      setStatus({
        kind: 'ready',
        identity: tokenResult.identity,
        twimlAppSid: tokenResult.twimlAppSid,
        tokenExpiresAt: tokenResult.expiresAt,
        recordingMode: tokenResult.recordingMode ?? 'off',
        playRecordingDisclosure:
          tokenResult.effectiveVoiceConfig?.playRecordingDisclosure ?? false,
        // Server-authoritative. Fail closed: anything but a literal true is false.
        incomingAllow: tokenResult.incomingAllow === true,
        // Option B — 'outbound' when the CRM voice-token controller
        // proxied the mint to Outbound; 'crm' or absent for native.
        managedBy: tokenResult.managedBy,
      });
    });

    device.on('unregistered', () => {
      if (!isMountedRef.current) return;
      // Don't override a more-specific error state. If we were ready
      // and the device unregistered, fall back to a generic error so
      // the user can retry.
      setStatus((prev) =>
        prev.kind === 'ready'
          ? {
              kind: 'device_error',
              message: 'The Twilio Voice device unregistered. Click Retry.',
            }
          : prev
      );
    });

    device.on('error', (...args: unknown[]) => {
      if (!isMountedRef.current) return;
      const err = args[0];
      // Mirror to console so the developer can see the raw SDK error
      // (it usually has a `code` like 20101 / 20104 / 31201 that maps
      // directly to a Twilio docs page).
      console.error('[useTwilioVoiceDevice] Device "error" event:', err);
      setStatus({
        kind: 'device_error',
        message: formatTwilioError(err, 'Twilio Voice device error'),
      });
    });

    // Token refresh — Twilio fires this ~30s before expiration.
    // Re-fetch silently and update the same device instance so any
    // active call survives the rotation.
    device.on('tokenWillExpire', async () => {
      if (!isMountedRef.current) return;
      try {
        // Re-mint against the SAME provider the current device is bound
        // to — never silently drop back to the workspace default across
        // a token rotation. `activeProviderIdRef` is the source of
        // truth; the refreshed token echo is re-verified below so a
        // mid-flight provider change (e.g., an operator suspended on
        // Outbound) surfaces as `TOKEN_PROVIDER_MISMATCH` instead of
        // an invisible account swap.
        const hint = activeProviderIdRef.current;
        const next = await fetchVoiceToken(
          hint ? { outboundProviderId: hint } : undefined,
        ).unwrap();
        if (!isMountedRef.current) return;
        // Silently swallowing a wrong-provider echo here would let a
        // rotated token relocate the browser onto a different Twilio
        // account without any UI signal. Refuse the rotation instead —
        // the next Device.connect() would fail the echo check anyway,
        // and this collapses the failure surface to a single place.
        if (hint) {
          const echoed =
            typeof (next as { outboundProviderId?: string }).outboundProviderId ===
              'string' &&
            (next as { outboundProviderId?: string }).outboundProviderId!.length > 0
              ? (next as { outboundProviderId?: string }).outboundProviderId
              : null;
          if (echoed !== hint) {
            setStatus({
              kind: 'device_error',
              message:
                'Token refresh returned a different provider than the current selection. Please re-select a calling provider.',
            });
            return;
          }
        }
        device.updateToken(next.token);
        setStatus((prev) =>
          prev.kind === 'ready'
            ? { ...prev, tokenExpiresAt: next.expiresAt }
            : prev
        );
      } catch (err) {
        if (!isMountedRef.current) return;
        setStatus(mapTokenErrorToStatus(err));
      }
    });

    deviceRef.current = device;
    registeredWorkspaceIdRef.current = workspaceId;

    // ADDITIVE (inbound calling, Phase IV). Hand the Device to the
    // TelephonyProvider — and only the provider — BEFORE register(), so an
    // incoming call arriving the instant registration completes still finds a
    // listener attached. Nobody else receives this reference; the Device never
    // appears on the public context.
    try {
      onDeviceChangeRef.current?.(device);
    } catch {
      // A provider-side listener bug must never break device registration.
    }

    // 6. Register. The 'registered' event handler above flips status
    //    to 'ready' on success.
    //
    // RACE CONDITION NOTE — known Twilio SDK quirk:
    // When registration fails because of a signaling error (e.g., 31202
    // AccessTokenSignatureValidationFailed, 31201 AuthenticationFailed,
    // 31204 AccessTokenInvalid, 31205 AccessTokenExpired), the SDK fires
    // its 'error' event FIRST with a properly-typed TwilioError, and
    // then the `register()` Promise rejects with `undefined`. If we
    // overwrite our status here from `register()`'s catch block, we
    // clobber the specific message the 'error' handler already set
    // (e.g. "Twilio Voice device error: code 31202 — AccessTokenSignatureValidationFailed
    // — The provided access token failed signature validation") with a
    // generic "Failed to register device." which is useless.
    //
    // So: only overwrite if we're NOT already in device_error. If the
    // 'error' event already moved us to device_error, keep that
    // (already-specific) message.
    try {
      await device.register();
    } catch (err) {
      if (!isMountedRef.current) return;
      console.error('[useTwilioVoiceDevice] Device.register() failed:', err);
      setStatus((prev) => {
        if (prev.kind === 'device_error') {
          // The 'error' event handler already set a specific message.
          // Don't overwrite it with the generic register-rejection one.
          return prev;
        }
        return {
          kind: 'device_error',
          message: formatTwilioError(err, 'Failed to register device'),
        };
      });
    }
  }, [
    canMakeCalls,
    canViewCalls,
    workspaceId,
    telephonyLoading,
    telephonyReason,
    fetchVoiceToken,
    suspended,
  ]);

  // ----------------------------------------------------------------
  // Mount / unmount + workspace switch
  // ----------------------------------------------------------------
  useEffect(() => {
    isMountedRef.current = true;
    void initialize();

    return () => {
      isMountedRef.current = false;
      // Tear down any active call first so its listeners don't fire
      // after unmount.
      if (callRef.current) {
        try {
          callRef.current.removeAllListeners();
          callRef.current.disconnect();
        } catch {
          // ignore
        }
        callRef.current = null;
      }
      if (tickIntervalRef.current !== null) {
        window.clearInterval(tickIntervalRef.current);
        tickIntervalRef.current = null;
      }
      // Tear down any device we created.
      if (deviceRef.current) {
        try {
          deviceRef.current.removeAllListeners();
          deviceRef.current.destroy();
        } catch {
          // ignore
        }
        deviceRef.current = null;
        try {
          onDeviceChangeRef.current?.(null);
        } catch {
          // ignore
        }
      }
      registeredWorkspaceIdRef.current = null;
    };
    // initialize is stable per the deps above; React knows to
    // re-run this effect on workspace switch / capability change.
  }, [initialize]);

  // ----------------------------------------------------------------
  // Subscribe to mic permission changes (user toggles in browser settings)
  // ----------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    let unsub: () => void = () => {};

    void subscribeToMicrophonePermission((next) => {
      if (cancelled) return;
      setMicState(next);
      // If permission was just granted while we were stuck in mic_prompt
      // or mic_denied, re-walk the lifecycle.
      if (next === 'granted') {
        void initialize();
      } else if (next === 'denied') {
        setStatus({ kind: 'mic_denied' });
      }
    }).then((u) => {
      if (cancelled) {
        u();
      } else {
        unsub = u;
      }
    });

    return () => {
      cancelled = true;
      unsub();
    };
  }, [initialize]);

  // ----------------------------------------------------------------
  // Public API
  // ----------------------------------------------------------------
  const refresh = useCallback(async () => {
    await initialize();
  }, [initialize]);

  // ----------------------------------------------------------------
  // Per-provider browser calling
  // ----------------------------------------------------------------
  //
  // Re-mint the Voice access token against `outboundProviderId` and
  // rebuild the Twilio Device so subsequent `Device.connect()` calls
  // route through THAT provider's Twilio account + TwiML App.
  //
  // Sequencing rule (enforced by ContactWorkspaceCallColumn): the caller
  // MUST await this before calling `connectToOutboundCall`. Without the
  // wait, `Device.connect` runs on the previous provider's token and
  // the lead-leg ends up on the wrong Twilio account.
  //
  // Fail-closed behaviour:
  //   - No id supplied              → OUTBOUND_PROVIDER_ID_MISSING
  //   - Same id as current device    → return ok immediately (no remint)
  //   - Mint fails                   → typed code from the server surface
  //   - Mint OK but echo mismatch    → TOKEN_PROVIDER_MISMATCH
  //   - SDK rebuild fails            → DEVICE_REBUILD_FAILED
  const switchProvider = useCallback(
    async (outboundProviderId: string): Promise<SwitchProviderResult> => {
      const requestedProviderId = outboundProviderId;
      if (
        typeof requestedProviderId !== 'string' ||
        requestedProviderId.length === 0
      ) {
        return {
          ok: false,
          code: 'OUTBOUND_PROVIDER_ID_MISSING',
          message: 'A provider id is required to switch calling providers.',
        };
      }

      // Fast path — the current Device is already bound to this provider.
      // Skip the destroy/remint/register churn (and the ready→registering
      // status flicker) so repeated selects of the same option are cheap.
      const currentStatus = statusRef.current;
      if (
        currentProviderIdRef.current === requestedProviderId &&
        currentStatus.kind === 'ready'
      ) {
        return {
          ok: true,
          outboundProviderId: requestedProviderId,
          twimlAppSid: currentStatus.twimlAppSid,
        };
      }

      // Stash the pick BEFORE calling initialize() so the token mint sees it.
      activeProviderIdRef.current = requestedProviderId;

      // Walk the full lifecycle: destroy the old Device (bound to the
      // wrong provider), mint against the new provider, construct + register
      // a fresh Device. `initialize()` handles every failure mode by
      // pushing an appropriate status; we inspect it after the call to
      // return a typed switch result.
      try {
        await initialize();
      } catch (err) {
        return {
          ok: false,
          code: 'DEVICE_REBUILD_FAILED',
          message:
            err instanceof Error
              ? `Failed to rebuild Voice device: ${err.message}`
              : 'Failed to rebuild Voice device.',
          requestedProviderId,
        };
      }
      if (!isMountedRef.current) {
        return {
          ok: false,
          code: 'DEVICE_REBUILD_FAILED',
          message: 'Component unmounted mid-switch.',
          requestedProviderId,
        };
      }

      // `initialize()` finishes as soon as `device.register()` resolves,
      // but the 'ready' status is set from Twilio's async 'registered'
      // event fired AFTER register() returns. Wait for the status to
      // settle (ready OR any terminal error) before returning so the
      // caller sees the new provider's twimlAppSid instead of the stale
      // pre-switch one. Bounded so a wedged SDK does not hang the button.
      const READY_WAIT_MS = 2000;
      const READY_POLL_MS = 20;
      const started = performance.now();
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const kind = statusRef.current.kind;
        if (
          kind === 'ready' ||
          kind === 'device_error' ||
          kind === 'token_error' ||
          kind === 'token_unauthorized' ||
          kind === 'capability_denied' ||
          kind === 'twilio_not_configured' ||
          kind === 'twilio_managed_by_outbound' ||
          kind === 'twilio_managed_by_outbound_unavailable' ||
          kind === 'twilio_voip_not_configured'
        ) {
          break;
        }
        if (performance.now() - started > READY_WAIT_MS) break;
        await new Promise((r) => setTimeout(r, READY_POLL_MS));
      }

      // Echo check: the mint must have returned the exact provider we
      // asked for. `initialize()` writes the echoed id to
      // `currentProviderIdRef` after a successful mint.
      const echoed = currentProviderIdRef.current;
      if (echoed !== requestedProviderId) {
        return {
          ok: false,
          code: 'TOKEN_PROVIDER_MISMATCH',
          message:
            'The freshly-minted Voice token is bound to a different Twilio provider than was requested. Refusing to place the call.',
          requestedProviderId,
          echoedProviderId: echoed ?? undefined,
        };
      }

      // Any non-ready status here is a failure surface `initialize()`
      // already wrote. Translate to typed codes for the caller. Read
      // via the ref — React hasn't re-rendered this closure yet.
      const finalStatus = statusRef.current;
      if (finalStatus.kind !== 'ready') {
        const readyLike = finalStatus as {
          kind: string;
          message?: string;
          reason?: string;
        };
        const mapped: SwitchProviderErrorCode =
          readyLike.kind === 'twilio_managed_by_outbound_unavailable'
            ? 'TWILIO_MANAGED_BY_OUTBOUND_UNAVAILABLE'
            : readyLike.kind === 'token_error' ||
                readyLike.kind === 'token_unauthorized'
              ? 'TOKEN_ERROR'
              : 'DEVICE_REBUILD_FAILED';
        return {
          ok: false,
          code: mapped,
          message:
            readyLike.message ??
            'Voice device is not ready after provider switch.',
          requestedProviderId,
        };
      }

      return {
        ok: true,
        outboundProviderId: requestedProviderId,
        twimlAppSid: finalStatus.twimlAppSid,
      };
    },
    [initialize],
  );

  // ----------------------------------------------------------------
  // Active call helpers (Phase 3)
  // ----------------------------------------------------------------

  /**
   * Stop and clear the elapsed-duration tick interval. Idempotent.
   */
  const stopTick = useCallback(() => {
    if (tickIntervalRef.current !== null) {
      window.clearInterval(tickIntervalRef.current);
      tickIntervalRef.current = null;
    }
  }, []);

  /**
   * Detach Call event listeners and drop the ref. Called on disconnect,
   * failure, and unmount. Safe to call when no call is active.
   */
  const teardownCall = useCallback(() => {
    stopTick();
    if (callRef.current) {
      try {
        callRef.current.removeAllListeners();
      } catch {
        // ignore
      }
      callRef.current = null;
    }
  }, [stopTick]);

  /**
   * ADDITIVE (inbound calling, Phase IV) — provider-only.
   *
   * Adopt an ALREADY-ACCEPTED inbound Call into the same `activeCall` state
   * machine that outgoing calls use. This is the whole reason there is no
   * second in-call implementation: once the Call lands in `callRef`, the
   * existing `mute`, `unmute`, `endCall`, `sendDigits`, `setInputDevice`,
   * `setOutputDevice` and the duration timer all operate on it unchanged, and
   * the existing ActiveCallShell renders it unchanged.
   *
   * The Call is accepted by the provider BEFORE this is called, so we go
   * straight to `in_call` and start the timer — there is no 'accept' event
   * still to come.
   */
  const attachIncomingCall = useCallback(
    (call: TwilioCall, meta: IncomingCallMeta) => {
      const answeredAt = Date.now();
      callRef.current = call;

      setActiveCall({
        kind: 'in_call',
        toNumber: meta.fromPhone,
        contactId: meta.contactId,
        contactName: meta.contactName,
        startedAt: answeredAt,
        answeredAt,
        muted: false,
        durationSeconds: 0,
        direction: 'inbound',
        twilioCallSid:
          typeof call.parameters?.CallSid === 'string'
            ? call.parameters.CallSid
            : undefined,
      });

      stopTick();
      tickIntervalRef.current = window.setInterval(() => {
        if (!isMountedRef.current) {
          stopTick();
          return;
        }
        setActiveCall((prev) =>
          prev.kind === 'in_call'
            ? {
                ...prev,
                durationSeconds: Math.floor((Date.now() - prev.answeredAt) / 1000),
              }
            : prev
        );
      }, 1000);

      call.on('disconnect', () => {
        if (!isMountedRef.current) return;
        setActiveCall((prev) => ({
          kind: 'ended',
          toNumber: meta.fromPhone,
          contactId: meta.contactId,
          contactName: meta.contactName,
          durationSeconds: prev.kind === 'in_call' ? prev.durationSeconds : 0,
          direction: 'inbound',
        }));
        teardownCall();
      });

      call.on('error', (...args: unknown[]) => {
        if (!isMountedRef.current) return;
        const err = args[0] as { message?: string } | undefined;
        setActiveCall({
          kind: 'failed',
          toNumber: meta.fromPhone,
          contactId: meta.contactId,
          contactName: meta.contactName,
          // Generic + safe: never surface a raw Twilio payload to the user.
          message: err?.message ? `Call failed: ${err.message}` : 'Call failed.',
          direction: 'inbound',
        });
        teardownCall();
      });

      call.on('mute', (...args: unknown[]) => {
        if (!isMountedRef.current) return;
        const isMuted = Boolean(args[0]);
        setActiveCall((prev) =>
          prev.kind === 'in_call' ? { ...prev, muted: isMuted } : prev
        );
      });

      // Media re-establishment. Both events are real in @twilio/voice-sdk
      // 2.18.1 (Call.EventName.Reconnecting / .Reconnected) — verified against
      // the installed type definitions, not assumed.
      call.on('reconnecting', () => {
        if (!isMountedRef.current) return;
        setActiveCall((prev) =>
          prev.kind === 'in_call' ? { ...prev, reconnecting: true } : prev
        );
      });

      call.on('reconnected', () => {
        if (!isMountedRef.current) return;
        setActiveCall((prev) =>
          prev.kind === 'in_call' ? { ...prev, reconnecting: false } : prev
        );
      });
    },
    [stopTick, teardownCall]
  );

  const connectToNumber = useCallback(
    async (toNumber: string, ctx?: ConnectCallContext) => {
      // Hard preconditions. We surface these as thrown errors so the UI
      // (PhonePage / contact actions) can show a toast — they are
      // programmer/UX bugs, not user-visible failures we want to render
      // in the call shell.
      if (!deviceRef.current) {
        throw new Error('Voice device is not initialized.');
      }
      if (status.kind !== 'ready') {
        throw new Error(`Voice device is not ready (state: ${status.kind}).`);
      }
      if (callRef.current) {
        throw new Error('A call is already in progress.');
      }
      const trimmed = toNumber.trim();
      if (!trimmed) {
        throw new Error('Destination phone number is required.');
      }

      const startedAt = Date.now();
      setActiveCall({
        kind: 'connecting',
        toNumber: trimmed,
        contactId: ctx?.contactId,
        contactName: ctx?.contactName,
        startedAt,
      });

      let call: TwilioCall;
      try {
        // Custom param `PhoneNumber` is read by the server's
        // handleVoiceTwiml — we deliberately avoid colliding with
        // Twilio's own `To` (which gets set to the application SID for
        // browser-client calls). See twilioVoiceTwimlController.ts.
        //
        // ADDITIVE (per-call outgoing recording). Send `Record` only when
        // the caller explicitly expressed a preference for THIS call.
        // Absent → the server falls through to the existing workspace
        // default so pre-change callers see byte-identical behaviour.
        const dialParams: Record<string, string> = { PhoneNumber: trimmed };
        if (ctx?.record === true) dialParams.Record = 'true';
        else if (ctx?.record === false) dialParams.Record = 'false';
        call = await deviceRef.current.connect({
          params: dialParams,
        });
      } catch (err) {
        if (!isMountedRef.current) return;
        setActiveCall({
          kind: 'failed',
          toNumber: trimmed,
          contactId: ctx?.contactId,
          contactName: ctx?.contactName,
          message:
            err instanceof Error
              ? `Failed to start call: ${err.message}`
              : 'Failed to start call.',
        });
        return;
      }

      callRef.current = call;

      // 'accept' fires when the destination answers. We start the
      // duration timer here so the displayed elapsed time matches the
      // billed call duration (Twilio doesn't bill for ringing).
      call.on('accept', () => {
        if (!isMountedRef.current) return;
        const answeredAt = Date.now();
        setActiveCall({
          kind: 'in_call',
          toNumber: trimmed,
          contactId: ctx?.contactId,
          contactName: ctx?.contactName,
          startedAt,
          answeredAt,
          muted: false,
          durationSeconds: 0,
          twilioCallSid:
            typeof call.parameters?.CallSid === 'string'
              ? call.parameters.CallSid
              : undefined,
        });

        // Drive the elapsed-duration counter once per second. We use
        // setInterval (not requestAnimationFrame) because the displayed
        // value updates at second granularity — wasteful to repaint at
        // 60fps for a clock that ticks once a second.
        stopTick();
        tickIntervalRef.current = window.setInterval(() => {
          if (!isMountedRef.current) {
            stopTick();
            return;
          }
          setActiveCall((prev) =>
            prev.kind === 'in_call'
              ? {
                  ...prev,
                  durationSeconds: Math.floor((Date.now() - prev.answeredAt) / 1000),
                }
              : prev
          );
        }, 1000);
      });

      // 'disconnect' fires when either side hangs up cleanly.
      call.on('disconnect', () => {
        if (!isMountedRef.current) return;
        setActiveCall((prev) => {
          const duration =
            prev.kind === 'in_call'
              ? prev.durationSeconds
              : 0;
          return {
            kind: 'ended',
            toNumber: trimmed,
            contactId: ctx?.contactId,
            contactName: ctx?.contactName,
            durationSeconds: duration,
          };
        });
        teardownCall();
      });

      // 'cancel' fires if the call was aborted before connecting (e.g.
      // user hangs up while it's still ringing).
      call.on('cancel', () => {
        if (!isMountedRef.current) return;
        setActiveCall({
          kind: 'ended',
          toNumber: trimmed,
          contactId: ctx?.contactId,
          contactName: ctx?.contactName,
          durationSeconds: 0,
        });
        teardownCall();
      });

      // 'error' fires for any SDK-level error during the call (network
      // drop, codec mismatch, signaling failure, etc.).
      call.on('error', (...args: unknown[]) => {
        if (!isMountedRef.current) return;
        const err = args[0] as { message?: string; code?: number } | undefined;
        setActiveCall({
          kind: 'failed',
          toNumber: trimmed,
          contactId: ctx?.contactId,
          contactName: ctx?.contactName,
          message: err?.message
            ? `Call failed: ${err.message}`
            : 'Call failed due to an unknown error.',
        });
        teardownCall();
      });

      // 'mute' fires whenever Call.mute(bool) is called — useful when
      // the user mutes via a hardware key on Bluetooth headsets.
      call.on('mute', (...args: unknown[]) => {
        if (!isMountedRef.current) return;
        const isMuted = Boolean(args[0]);
        setActiveCall((prev) =>
          prev.kind === 'in_call' ? { ...prev, muted: isMuted } : prev
        );
      });
    },
    [status, stopTick, teardownCall]
  );

  // Sibling of connectToNumber for Outbound-managed calls. Same
  // preconditions, same state machine, same lifecycle events — the ONLY
  // difference is the custom params passed to Device.connect(): here we
  // send `To` + `CallId` which the existing Outbound TwiML handler
  // resolves via `OutboundMessage.findByPk(CallId)`.
  const connectToOutboundCall = useCallback(
    async (
      { callId, toNumber }: { callId: string; toNumber: string },
      ctx?: ConnectCallContext,
    ) => {
      if (!deviceRef.current) {
        throw new Error('Voice device is not initialized.');
      }
      if (status.kind !== 'ready') {
        throw new Error(`Voice device is not ready (state: ${status.kind}).`);
      }
      if (callRef.current) {
        throw new Error('A call is already in progress.');
      }
      const trimmed = toNumber.trim();
      if (!trimmed) {
        throw new Error('Destination phone number is required.');
      }
      if (!callId) {
        throw new Error('Outbound call id is required.');
      }

      const startedAt = Date.now();
      setActiveCall({
        kind: 'connecting',
        toNumber: trimmed,
        contactId: ctx?.contactId,
        contactName: ctx?.contactName,
        startedAt,
        // Phase 1 linking — carry the outboundCallId (== prepare.callId)
        // on the ActiveCall state so downstream UI (ActiveCallRecordingCard
        // → useActiveCallRecording → POST /recording/start) can send it
        // to the backend and link the CallRecording to its parent
        // placeholder CallSession.
        outboundCallId: callId,
      });

      let call: TwilioCall;
      try {
        call = await deviceRef.current.connect({
          params: { To: trimmed, CallId: callId },
        });
      } catch (err) {
        if (!isMountedRef.current) return;
        setActiveCall({
          kind: 'failed',
          toNumber: trimmed,
          contactId: ctx?.contactId,
          contactName: ctx?.contactName,
          message:
            err instanceof Error
              ? `Failed to start call: ${err.message}`
              : 'Failed to start call.',
        });
        return;
      }

      callRef.current = call;

      // Wire the same listeners the connectToNumber path uses. Any
      // future divergence should stay in sync — both paths drive the
      // same activeCall state machine.
      call.on('accept', () => {
        if (!isMountedRef.current) return;
        const answeredAt = Date.now();
        setActiveCall({
          kind: 'in_call',
          toNumber: trimmed,
          contactId: ctx?.contactId,
          contactName: ctx?.contactName,
          startedAt,
          answeredAt,
          muted: false,
          durationSeconds: 0,
          twilioCallSid:
            typeof call.parameters?.CallSid === 'string'
              ? call.parameters.CallSid
              : undefined,
          // Phase 1 linking — same rationale as the 'connecting' state
          // above; downstream recording UI reads this to link the new
          // CallRecording to the placeholder CallSession.
          outboundCallId: callId,
        });
        stopTick();
        tickIntervalRef.current = window.setInterval(() => {
          if (!isMountedRef.current) {
            stopTick();
            return;
          }
          setActiveCall((prev) =>
            prev.kind === 'in_call'
              ? {
                  ...prev,
                  durationSeconds: Math.floor((Date.now() - prev.answeredAt) / 1000),
                }
              : prev,
          );
        }, 1000);
      });

      call.on('disconnect', () => {
        if (!isMountedRef.current) return;
        setActiveCall((prev) => {
          const duration = prev.kind === 'in_call' ? prev.durationSeconds : 0;
          return {
            kind: 'ended',
            toNumber: trimmed,
            contactId: ctx?.contactId,
            contactName: ctx?.contactName,
            durationSeconds: duration,
          };
        });
        teardownCall();
      });

      call.on('cancel', () => {
        if (!isMountedRef.current) return;
        setActiveCall({
          kind: 'ended',
          toNumber: trimmed,
          contactId: ctx?.contactId,
          contactName: ctx?.contactName,
          durationSeconds: 0,
        });
        teardownCall();
      });

      call.on('error', (...args: unknown[]) => {
        if (!isMountedRef.current) return;
        const err = args[0] as { message?: string; code?: number } | undefined;
        setActiveCall({
          kind: 'failed',
          toNumber: trimmed,
          contactId: ctx?.contactId,
          contactName: ctx?.contactName,
          message: err?.message
            ? `Call failed: ${err.message}`
            : 'Call failed due to an unknown error.',
        });
        teardownCall();
      });

      call.on('mute', (...args: unknown[]) => {
        if (!isMountedRef.current) return;
        const isMuted = Boolean(args[0]);
        setActiveCall((prev) =>
          prev.kind === 'in_call' ? { ...prev, muted: isMuted } : prev,
        );
      });
    },
    [status, stopTick, teardownCall],
  );

  const mute = useCallback(() => {
    if (callRef.current) {
      try {
        callRef.current.mute(true);
      } catch {
        // ignore — call may have already ended
      }
    }
  }, []);

  const unmute = useCallback(() => {
    if (callRef.current) {
      try {
        callRef.current.mute(false);
      } catch {
        // ignore
      }
    }
  }, []);

  const endCall = useCallback(() => {
    if (callRef.current) {
      try {
        callRef.current.disconnect();
      } catch {
        // The 'disconnect' event handler is responsible for state
        // transitions, so we don't update activeCall here directly.
      }
    }
  }, []);

  const clearActiveCall = useCallback(() => {
    setActiveCall({ kind: 'idle' });
  }, []);

  // ----------------------------------------------------------------
  // Phase-2B DTMF + Phase-3 audio device selection
  // ----------------------------------------------------------------

  /**
   * Send DTMF touch-tones over the CURRENT active call. Accepts only
   * 0-9, * and #. No-op (returns false) when there is no active call or the
   * input is invalid. Never throws into the render tree. The raw Twilio Call
   * object is never exposed — components call this action only.
   */
  const sendDigits = useCallback((digits: string): boolean => {
    const call = callRef.current;
    if (!call || typeof call.sendDigits !== 'function') return false;
    if (typeof digits !== 'string' || digits.length === 0) return false;
    if (!/^[0-9*#]+$/.test(digits)) return false;
    try {
      call.sendDigits(digits);
      return true;
    } catch {
      return false;
    }
  }, []);

  /** Re-enumerate mic/speaker devices from the shared Device's AudioHelper. */
  const refreshAudioDevices = useCallback(async (): Promise<void> => {
    const audio = deviceRef.current?.audio;
    if (!audio) {
      if (isMountedRef.current) setAudioDevices(EMPTY_AUDIO_DEVICES);
      return;
    }
    try {
      const toOptions = (m: Map<string, MediaDeviceInfo>): AudioDeviceOption[] =>
        Array.from(m.values()).map((d, i) => ({
          deviceId: d.deviceId,
          // Labels are blank until the mic permission is granted; give a
          // stable fallback so the selector still renders something.
          label: d.label || `Device ${i + 1}`,
        }));
      const inputs = toOptions(audio.availableInputDevices);
      const outputSelectionSupported = audio.isOutputSelectionSupported === true;
      const outputs = outputSelectionSupported ? toOptions(audio.availableOutputDevices) : [];
      const selectedInputId = audio.inputDevice?.deviceId ?? null;
      let selectedOutputId: string | null = null;
      if (outputSelectionSupported) {
        const active = Array.from(audio.speakerDevices.get().values());
        selectedOutputId = active[0]?.deviceId ?? null;
      }
      if (!isMountedRef.current) return;
      setAudioDevices({
        inputs,
        outputs,
        selectedInputId,
        selectedOutputId,
        outputSelectionSupported,
        loading: false,
        error: null,
      });
    } catch (err) {
      if (!isMountedRef.current) return;
      setAudioDevices((prev) => ({
        ...prev,
        loading: false,
        error: err instanceof Error ? err.message : 'Failed to read audio devices.',
      }));
    }
  }, []);

  /** Select the active microphone via the SDK. Returns false on failure. */
  const setInputDevice = useCallback(
    async (deviceId: string): Promise<boolean> => {
      const audio = deviceRef.current?.audio;
      if (!audio || typeof audio.setInputDevice !== 'function' || !deviceId) return false;
      try {
        await audio.setInputDevice(deviceId);
        await refreshAudioDevices();
        return true;
      } catch (err) {
        // Scoped error only — never terminates the call.
        if (isMountedRef.current) {
          setAudioDevices((prev) => ({
            ...prev,
            error: err instanceof Error ? err.message : 'Failed to select microphone.',
          }));
        }
        return false;
      }
    },
    [refreshAudioDevices],
  );

  /**
   * Select the active speaker via the SDK. Returns false when output
   * selection is unsupported (Safari / Firefox) — the UI honestly hides the
   * selector in that case, so this is only reached on supporting browsers.
   */
  const setOutputDevice = useCallback(
    async (deviceId: string): Promise<boolean> => {
      const audio = deviceRef.current?.audio;
      if (!audio || audio.isOutputSelectionSupported !== true || !deviceId) return false;
      try {
        await audio.speakerDevices.set(deviceId);
        await refreshAudioDevices();
        return true;
      } catch (err) {
        if (isMountedRef.current) {
          setAudioDevices((prev) => ({
            ...prev,
            error: err instanceof Error ? err.message : 'Failed to select speaker.',
          }));
        }
        return false;
      }
    },
    [refreshAudioDevices],
  );

  // Wire the AudioHelper `deviceChange` listener ONCE per ready device.
  // Runs when readiness flips to 'ready'; the cleanup removes exactly the
  // listener we added, so device replacement / token refresh / contact switch
  // / unmount never leak or duplicate listeners.
  useEffect(() => {
    if (status.kind !== 'ready') return;
    const audio = deviceRef.current?.audio;
    if (!audio) return;
    const handler = () => {
      void refreshAudioDevices();
    };
    audioChangeHandlerRef.current = handler;
    try {
      audio.on('deviceChange', handler);
    } catch {
      /* ignore — SDK without deviceChange still enumerates on demand */
    }
    void refreshAudioDevices();
    return () => {
      if (audio.removeListener && audioChangeHandlerRef.current) {
        try {
          audio.removeListener('deviceChange', audioChangeHandlerRef.current);
        } catch {
          /* ignore */
        }
      }
      audioChangeHandlerRef.current = null;
    };
  }, [status.kind, refreshAudioDevices]);

  const requestMicAccess = useCallback(async () => {
    setStatus({ kind: 'checking_mic' });
    const next = await requestMicrophonePermission();
    setMicState(next);
    if (next === 'granted') {
      await initialize();
    } else if (next === 'denied') {
      setStatus({ kind: 'mic_denied' });
    } else if (next === 'unsupported') {
      setStatus({ kind: 'mic_unsupported' });
    } else {
      setStatus({ kind: 'mic_prompt' });
    }
  }, [initialize]);

  return {
    status,
    isReady: status.kind === 'ready',
    // ADDITIVE: stamp `direction` on every non-idle state. Outgoing calls never
    // set it (we deliberately left all pre-existing setActiveCall call sites
    // untouched), so absent === 'outbound'. attachIncomingCall sets 'inbound'
    // explicitly and that survives this.
    activeCall:
      activeCall.kind === 'idle'
        ? activeCall
        : { ...activeCall, direction: activeCall.direction ?? 'outbound' },
    refresh,
    requestMicAccess,
    connectToNumber,
    connectToOutboundCall,
    switchProvider,
    mute,
    unmute,
    endCall,
    clearActiveCall,
    sendDigits,
    audioDevices,
    setInputDevice,
    setOutputDevice,
    refreshAudioDevices,
    // Provider-only. Not on UseTwilioVoiceDeviceResult, so it is stripped from
    // the public context value the wrapper below hands to consumers.
    attachIncomingCall,
  };

  // Suppress unused warning if dev tooling complains about micState —
  // it's intentionally tracked so the subscription effect can react.
  void micState;
}

/**
 * Public hook — UNCHANGED NAME, UNCHANGED SIGNATURE, UNCHANGED RETURN TYPE.
 *
 * This is the compatibility adapter that lets the global TelephonyProvider own
 * exactly one Device without touching a single existing call site:
 *
 *   - Inside a TelephonyProvider (the authenticated app shell), it returns the
 *     provider's shared value, and the local engine is suspended — no second
 *     Device, no second registration, no duplicate listeners.
 *   - Outside a provider (standalone tests, or any surface mounted outside the
 *     shell), the local engine runs exactly as it always has.
 *
 * Every existing consumer — PhonePage, ContactQuickActions,
 * ContactDetailSlideOver, VoiceDeviceReadinessCard, ContactWorkspaceCallColumn —
 * keeps calling `useTwilioVoiceDevice()` and keeps getting the same shape.
 */
export function useTwilioVoiceDevice(): UseTwilioVoiceDeviceResult {
  const shared = useSharedTelephony();
  // Hooks cannot be called conditionally, so the engine always runs — but when
  // a provider is present it runs suspended, which makes it inert.
  const local = useTwilioVoiceDeviceEngine({ suspended: shared !== null });
  return shared ?? local;
}

// ============================================================
// Helpers
// ============================================================

/**
 * Map an RTK Query error from POST /api/telephony/voice/token into
 * the right discriminated VoiceDeviceStatus state.
 *
 * Server contract (Phase 1):
 *   401                       — auth expired
 *   403 CAPABILITY_DENIED     — Central capability missing
 *   503 TWILIO_NOT_CONFIGURED — workspace integration missing
 *   503 TWILIO_VOIP_NOT_CONFIGURED { missingFields }
 *
 * Anything else (network, 500, etc.) → token_error with the raw message
 * so the user can copy-paste it to support if needed.
 */
function mapTokenErrorToStatus(err: unknown): VoiceDeviceStatus {
  if (err && typeof err === 'object' && 'status' in err) {
    const statusCode = (err as { status?: number }).status;
    const data = (err as { data?: { code?: string; message?: string; missingFields?: string[] } })
      .data;

    if (statusCode === 401) {
      return {
        kind: 'token_unauthorized',
        message: 'Your session expired. Please refresh the page and sign in again.',
      };
    }

    if (data?.code === 'CAPABILITY_DENIED') {
      return {
        kind: 'capability_denied',
        message:
          'You do not have permission to make calls. Ask your workspace admin to grant phone.call.',
      };
    }
    if (data?.code === 'TWILIO_NOT_CONFIGURED') {
      return {
        kind: 'twilio_not_configured',
        message:
          data.message ||
          'Twilio is not configured for this workspace. Connect Twilio in Settings → Integrations.',
      };
    }
    if (data?.code === 'TWILIO_MANAGED_BY_OUTBOUND') {
      // Provider-Sync mirror row — credentials live in Outbound, not
      // CRM. The Providers page reports the same row as active/healthy;
      // the readiness card mirrors that truth instead of showing the
      // misleading "Twilio not configured" copy the legacy 503 caused.
      return {
        kind: 'twilio_managed_by_outbound',
        message:
          data.message ||
          'Twilio Voice for this workspace is managed by Outbound. Calling from CRM is not enabled for this provider.',
      };
    }
    if (data?.code === 'TWILIO_MANAGED_BY_OUTBOUND_UNAVAILABLE') {
      // Option B — CRM detected the Outbound-managed row, tried to
      // proxy to Outbound, and the proxy call failed. Surfaces the
      // specific reason (from the server's `reason` field) so admins
      // can act on it (e.g. TWIML_APP_MISSING).
      const reason =
        typeof (data as { reason?: unknown }).reason === 'string'
          ? (data as { reason: string }).reason
          : undefined;
      return {
        kind: 'twilio_managed_by_outbound_unavailable',
        message:
          data.message ||
          'Twilio Voice is managed by Outbound, but the token request could not be completed.',
        reason,
      };
    }
    if (data?.code === 'TWILIO_VOIP_NOT_CONFIGURED') {
      return {
        kind: 'twilio_voip_not_configured',
        message:
          data.message ||
          'Browser calling is not configured. Add API Key SID, API Key Secret, and TwiML App SID in Settings → Integrations → Twilio.',
        missingFields: data.missingFields || [],
      };
    }

    return {
      kind: 'token_error',
      message: data?.message || `Failed to fetch voice token (HTTP ${statusCode}).`,
    };
  }

  return {
    kind: 'token_error',
    message: err instanceof Error ? err.message : 'Failed to fetch voice token.',
  };
}

/**
 * Format any Twilio Voice SDK error (Error subclass, plain object with
 * `code`/`message`, or anything else) into a user-readable string AND
 * preserve the Twilio error code so the developer can look it up in
 * https://www.twilio.com/docs/api/errors. The Voice SDK frequently
 * rejects with plain objects (e.g. `{ code: 20101, message: 'Invalid
 * Access Token' }`), and `instanceof Error` would silently drop them.
 *
 * Common codes seen on Device.register() failures:
 *   20101 — Invalid Access Token (signature/issuer/audience mismatch)
 *   20104 — Access Token expired (clock skew or stale token)
 *   31005 — Connection error to Twilio's signaling endpoint
 *   31201 — Generic AudioInputDeviceMissing / mic revoked mid-flight
 *   31204 — Invalid token claims (e.g. missing VoiceGrant)
 */
function formatTwilioError(err: unknown, fallback: string): string {
  if (!err) return `${fallback}.`;

  if (err instanceof Error) {
    return `${fallback}: ${err.message}`;
  }

  if (typeof err === 'object') {
    const e = err as { code?: number | string; message?: string; name?: string };
    const parts: string[] = [];
    if (e.code !== undefined) parts.push(`code ${e.code}`);
    if (e.name) parts.push(e.name);
    if (e.message) parts.push(e.message);
    if (parts.length > 0) {
      return `${fallback}: ${parts.join(' — ')}`;
    }
    // Last resort: stringify so the user/dev can see SOMETHING.
    try {
      return `${fallback}: ${JSON.stringify(err)}`;
    } catch {
      return `${fallback}.`;
    }
  }

  if (typeof err === 'string') return `${fallback}: ${err}`;
  return `${fallback}.`;
}
